import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import ChecklistItem from "./ChecklistItem";
import GlossaryHint from "./GlossaryHint";
import FieldError from "./FieldError";
import { ChoiceGroup } from "./ChoiceCard";
import { formatNumberWithCommas, sanitizeNumberInput } from "@/lib/numberFormat";
import { INPUT_BOX_CLASS, INPUT_BOX_STYLE, ELIGIBILITY_PEER_DIM_STYLE } from "./checkerFieldStyles";
import { fieldAnchorProps, invalidInputProps } from "./checkerValidation";

const projectTypes = [
  "Digital Transformation", "Automation", "R&D", "Market Expansion",
  "Skills Development", "Sustainability", "Innovation", "Other"
];

/** Minimum length for the free-text project description (a real definition, not a list of goal labels). */
export const PROJECT_DESCRIPTION_MIN_CHARS = 80;

/** One-line definition of each Project Type option (shown in the glossary and next to the select). */
// eslint-disable-next-line react-refresh/only-export-components
export const PROJECT_TYPE_DEFINITIONS = {
  "Digital Transformation":
    "Adopting or upgrading internal systems, workflows and tools (for example ERP, CRM, back-office automation). Internal operations, not a product you sell.",
  Automation:
    "Replacing manual steps with software, machines or robotics to cut labour, time or error rates in an existing process.",
  "R&D":
    "Building something new where the technical outcome is uncertain: a new product, algorithm, material or process, including proof-of-concept work.",
  "Market Expansion":
    "Entering a new overseas market: market research, overseas marketing, partner or distributor search, or setting up an overseas entity.",
  "Skills Development":
    "Training, certifying or redesigning jobs for your existing workforce so people can do higher-value work.",
  Sustainability:
    "Cutting energy, water, waste or carbon, or meeting an ESG or green-certification requirement.",
  Innovation:
    "Launching a new product, service or business model to the market that is not primarily technical research.",
  Other:
    "Does not fit the types above. Describe it fully in the project description so it can be matched to the right scheme.",
};

/**
 * Plain-language glossary for the terms the assessment uses. The results page
 * and PDF echo the same definitions from the backend glossary; this copy keeps
 * the form self-explanatory before the report exists.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const PROJECT_GLOSSARY = [
  {
    term: "Project",
    definition:
      "A bounded piece of work with a start and end date, a defined scope, a budget and a measurable outcome. Examples: building a specific product feature, adopting a named system, entering one overseas market. Not a project: a company goal (\"grow revenue\"), an ongoing activity (\"run marketing\"), or hiring in general.",
  },
  {
    term: "Project type",
    definition:
      "The category that best describes the main activity of the project. It decides which scheme families are considered first (for example R&D routes versus productivity-solution routes).",
  },
  {
    term: "Project maturity",
    definition:
      "How far the project has progressed: proof of concept (testing whether it works), proof of value (testing whether it pays off), or implementation / scale-up (rolling it out).",
  },
  {
    term: "Co-funding capacity",
    definition:
      "The share of total project cost your company can pay itself. Most Singapore grants reimburse a percentage after you have paid, so this affects which schemes are realistic.",
  },
  {
    term: "Signal Match",
    definition:
      "A 0–100 score for how closely your company and project match a scheme's published criteria. Bands: Strong 72+, Promising 60–71, Partial 45–59, Weak below 45.",
  },
  {
    term: "Strategic Fit",
    definition:
      "A separate 0–100 score for whether this is the right scheme for this specific project, based on project type, maturity and use of funds — not just whether the company could qualify. High 72+, Medium 60–71, Low below 60.",
  },
];

const PROJECT_DEFINITION_EXAMPLES = {
  isProject: [
    "Build and launch a compliance-monitoring feature for our SaaS platform by Q2",
    "Adopt a named ERP system across finance and operations within 6 months",
    "Enter the Malaysian market with a distributor and a 3-month marketing campaign",
  ],
  notProject: [
    "Increase productivity or reduce costs (a goal, not a bounded piece of work)",
    "Hire more engineers (headcount, unless tied to a specific deliverable)",
    "Grow the business in the region (no scope, budget or end date)",
  ],
};

const glossaryFor = (term) => PROJECT_GLOSSARY.find((entry) => entry.term === term);

const timelineOptions = [
  "3-6 months", "6-12 months", "1-2 years", "2+ years"
];

const projectMaturityOptions = [
  { value: "Not specified", label: "Not specified" },
  { value: "Proof of concept (POC)", label: "Proof of concept (POC)" },
  { value: "Proof of value (POV)", label: "Proof of value (POV)" },
  { value: "Implementation / scale-up", label: "Implementation / scale-up" },
];

const objectiveOptions = [
  "Increase productivity", "Reduce costs", "Improve quality", "Enter new markets",
  "Enhance capabilities", "Upskill workforce", "Digital adoption", "Sustainability goals"
];

const businessFunctionOptions = [
  "Sales",
  "Marketing",
  "Operations",
  "Finance",
  "HR",
  "IT",
  "Customer Service",
  "Supply Chain",
  "Compliance",
  "Other"
];

const vendorCountryOptions = [
  "Singapore",
  "Overseas",
  "Multiple / Not sure"
];

const quotationCountOptions = [
  "None received / NIL",
  "1",
  "2",
  "3 or more",
  "Not applicable"
];

const canFundUpfrontOptions = [
  "Yes",
  "No",
  "Not sure"
];

const applicationPriorityOptions = [
  "Highest approval chance",
  "Maximum funding amount",
  "Fastest submission",
  "Fastest project start",
  "Not sure"
];

const scopeFlexibilityOptions = [
  "Yes",
  "No",
  "Maybe"
];

const pillStyle = {
  color: "#60A5FA",
  backgroundColor: "rgba(96, 165, 250, 0.10)",
  border: "1px solid rgba(96, 165, 250, 0.35)",
  boxShadow: "none",
};

const selectedPillStyle = {
  color: "#60A5FA",
  backgroundColor: "rgba(96, 165, 250, 0.14)",
  border: "1px solid rgba(96, 165, 250, 0.5)",
  boxShadow: "none",
};

const COFUNDING_QUICK_PICKS = [0, 25, 50, 75, 100];

/**
 * Label + custom select with the label association, anchor and inline error
 * the validation summary relies on. `options` may be strings or {value,label}.
 */
function LabelledSelect({ name, label, icon = null, value, onChange, options, placeholder, error, required = true }) {
  const labelId = `${name}-label`;
  return (
    <div className="space-y-2" {...fieldAnchorProps(name)}>
      <Label id={labelId} htmlFor={name} className="font-medium" style={{ color: '#4B5563' }}>
        {icon}
        {label}{required ? <span style={{ color: '#4B5563' }}> *</span> : null}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          id={name}
          aria-labelledby={labelId}
          className={INPUT_BOX_CLASS}
          style={INPUT_BOX_STYLE}
          {...invalidInputProps(name, error)}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => {
            const optValue = typeof opt === 'string' ? opt : opt.value;
            const optLabel = typeof opt === 'string' ? opt : opt.label;
            return (
              <SelectItem key={optValue} value={optValue}>{optLabel}</SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      <FieldError name={name} message={error} />
    </div>
  );
}

export default function ProjectStep({ formData, updateFormData, errors = {} }) {
  const addObjective = (objective) => {
    const currentObjectives = formData.project_objectives || [];
    if (!currentObjectives.includes(objective)) {
      updateFormData({ project_objectives: [...currentObjectives, objective] });
    }
  };

  const removeObjective = (objective) => {
    const currentObjectives = formData.project_objectives || [];
    updateFormData({ project_objectives: currentObjectives.filter(obj => obj !== objective) });
  };

  // No prefill: show empty until the user selects a value.
  // When "Not specified" is chosen, store/show exactly that label.
  const projectMaturitySelectValue =
    formData.project_maturity && formData.project_maturity !== ''
      ? (formData.project_maturity === 'unspecified' ? 'Not specified' : String(formData.project_maturity))
      : undefined;

  return (
    <div id="step-project" className="space-y-10">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-2" style={{ color: '#4B5563' }} tabIndex={-1} data-step-heading>Project Information</h2>
        <p style={{ color: '#4B5563' }}>Details about the project you're seeking funding for</p>
      </div>

      <div className="w-full rounded-3xl bg-white/5 backdrop-blur-xl shadow-2xl border border-white/10 p-6 md:p-8">
        <div className="grid gap-6">
        {/* What counts as a project — shown first so every field below is interpreted the same way */}
        <div
          className="checker-info-box space-y-3"
          data-testid="project-definition"
        >
          <p className="text-sm font-semibold" style={{ color: '#1F2937' }}>
            What OPTRA counts as a project
          </p>
          <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
            {glossaryFor('Project')?.definition}
          </p>
          <div className="grid gap-4 text-sm leading-relaxed md:grid-cols-2" style={{ color: '#4B5563' }}>
            <div>
              <p className="font-semibold mb-1" style={{ color: '#1F2937' }}>Counts as a project</p>
              <ul className="list-disc list-inside space-y-0.5">
                {PROJECT_DEFINITION_EXAMPLES.isProject.map((example) => (
                  <li key={example}>{example}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-semibold mb-1" style={{ color: '#1F2937' }}>Not a project on its own</p>
              <ul className="list-disc list-inside space-y-0.5">
                {PROJECT_DEFINITION_EXAMPLES.notProject.map((example) => (
                  <li key={example}>{example}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Type & Timeline */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2" {...fieldAnchorProps('project_type')}>
            <div className="flex items-center gap-2">
              <Label id="project_type-label" htmlFor="project_type" className="font-medium" style={{ color: '#4B5563' }}>
                Project Type <span style={{ color: '#4B5563' }}>*</span>
              </Label>
              <GlossaryHint term="Project type" definition={glossaryFor('Project type')?.definition} />
            </div>
            <Select value={formData.project_type || ''} onValueChange={(value) => updateFormData({ project_type: value })}>
              <SelectTrigger
                id="project_type"
                aria-labelledby="project_type-label"
                className={INPUT_BOX_CLASS}
                style={INPUT_BOX_STYLE}
                {...invalidInputProps('project_type', errors.project_type, 'project_type-hint')}
              >
                <SelectValue placeholder="Select project type" />
              </SelectTrigger>
              <SelectContent>
                {projectTypes.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError name="project_type" message={errors.project_type} />
            <p id="project_type-hint" className="text-xs leading-relaxed" style={{ color: '#94A3B8' }}>
              Be specific here, digital transformation usually means internal operations, workflows, and systems rather than a client-facing product.
            </p>
            {formData.project_type && PROJECT_TYPE_DEFINITIONS[formData.project_type] ? (
              <p className="text-xs leading-relaxed" style={{ color: '#4B5563' }} data-testid="project-type-definition">
                <span className="font-semibold">{formData.project_type}:</span> {PROJECT_TYPE_DEFINITIONS[formData.project_type]}
              </p>
            ) : null}
          </div>

          <div className="space-y-2" {...fieldAnchorProps('project_maturity')}>
            <div className="flex items-center gap-2">
              <Label id="project_maturity-label" htmlFor="project_maturity" className="font-medium" style={{ color: '#4B5563' }}>
                Project stage <span style={{ color: '#4B5563' }}>*</span>
              </Label>
              <GlossaryHint term="Project maturity" definition={glossaryFor('Project maturity')?.definition} />
            </div>
            <Select
              value={projectMaturitySelectValue}
              onValueChange={(value) => {
                updateFormData({
                  project_maturity: value,
                });
              }}
            >
              <SelectTrigger
                id="project_maturity"
                aria-labelledby="project_maturity-label"
                className={INPUT_BOX_CLASS}
                style={INPUT_BOX_STYLE}
                {...invalidInputProps('project_maturity', errors.project_maturity)}
              >
                <SelectValue placeholder="POC, POV, or implementation" />
              </SelectTrigger>
              <SelectContent>
                {projectMaturityOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError name="project_maturity" message={errors.project_maturity} />
          </div>

          {!formData.project_type ? (
            <details className="checker-project-type-details md:col-span-2" style={{ color: '#4B5563' }}>
              <summary className="cursor-pointer" style={{ color: '#60A5FA' }}>What each project type means</summary>
              <ul className="mt-3 grid gap-x-6 gap-y-2 md:grid-cols-2">
                {projectTypes.map((type) => (
                  <li key={type}>
                    <span className="font-semibold">{type}:</span> {PROJECT_TYPE_DEFINITIONS[type]}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}

          <div className="space-y-2 md:col-span-2" {...fieldAnchorProps('expected_timeline')}>
            <Label id="expected_timeline-label" htmlFor="expected_timeline" className="font-medium" style={{ color: '#4B5563' }}>
              Expected Timeline <span style={{ color: '#4B5563' }}>*</span>
            </Label>
            <Select value={formData.expected_timeline || ''} onValueChange={(value) => updateFormData({ expected_timeline: value })}>
              <SelectTrigger
                id="expected_timeline"
                aria-labelledby="expected_timeline-label"
                className={INPUT_BOX_CLASS}
                style={INPUT_BOX_STYLE}
                {...invalidInputProps('expected_timeline', errors.expected_timeline)}
              >
                <SelectValue placeholder="Select timeline" />
              </SelectTrigger>
              <SelectContent>
                {timelineOptions.map((timeline) => (
                  <SelectItem key={timeline} value={timeline}>{timeline}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError name="expected_timeline" message={errors.expected_timeline} />
          </div>
        </div>

        {/* Business function impacted */}
        <div className="space-y-2" {...fieldAnchorProps('business_function_impacted')}>
          <Label id="business_function_impacted-label" htmlFor="business_function_impacted" className="font-medium" style={{ color: '#4B5563' }}>
            Business Function Impacted <span style={{ color: '#4B5563' }}>*</span>
          </Label>
          <Select
            value={formData.business_function_impacted || ''}
            onValueChange={(value) => updateFormData({ business_function_impacted: value })}
          >
            <SelectTrigger
              id="business_function_impacted"
              aria-labelledby="business_function_impacted-label"
              className={INPUT_BOX_CLASS}
              style={INPUT_BOX_STYLE}
              {...invalidInputProps('business_function_impacted', errors.business_function_impacted)}
            >
              <SelectValue placeholder="Which function will this project impact most?" />
            </SelectTrigger>
            <SelectContent>
              {businessFunctionOptions.map((opt) => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError name="business_function_impacted" message={errors.business_function_impacted} />
        </div>

        {/* Budget */}
        <div className="space-y-2" {...fieldAnchorProps('project_budget_sgd')}>
          <Label htmlFor="budget" className="font-medium" style={{ color: '#4B5563' }}>
            Project Budget (SGD) <span style={{ color: '#4B5563' }}>*</span>
          </Label>
          <Input
            id="budget"
            name="project_budget_sgd"
            type="text"
            inputMode="numeric"
            placeholder="e.g., 100,000"
            value={formatNumberWithCommas(formData.project_budget_sgd)}
            onChange={(e) => {
              const sanitized = sanitizeNumberInput(e.target.value);
              updateFormData({ project_budget_sgd: sanitized });
            }}
            style={INPUT_BOX_STYLE}
            className={INPUT_BOX_CLASS}
            {...invalidInputProps('project_budget_sgd', errors.project_budget_sgd)}
          />
          <FieldError name="project_budget_sgd" message={errors.project_budget_sgd} />
        </div>

        {/* Objectives */}
        <ChoiceGroup
          name="project_objectives"
          role="group"
          legend="Project Objectives (click to add)"
          required
          error={errors.project_objectives}
          className="space-y-4"
          optionsClassName="space-y-4"
        >
          <div className="flex flex-wrap gap-2 mb-4" data-testid="project-objectives">
            {objectiveOptions.map((objective) => (
              <button
                key={objective}
                type="button"
                aria-pressed={formData.project_objectives?.includes(objective) || false}
                onClick={() => addObjective(objective)}
                className={`
                  inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 shadow-lg
                  ${formData.project_objectives?.includes(objective)
                    ? 'cursor-not-allowed opacity-60'
                    : ''
                  }
                `}
                style={pillStyle}
                disabled={formData.project_objectives?.includes(objective)}
              >
                {objective}
              </button>
            ))}
          </div>

          {formData.project_objectives?.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium" style={{ color: '#4B5563' }}>Selected objectives:</p>
              <div className="flex flex-wrap gap-2" data-testid="selected-objectives">
                {formData.project_objectives.map((objective) => (
                  <span
                    key={objective}
                    className="inline-flex items-center rounded-full pl-4 pr-2 py-1 text-sm font-medium transition-all duration-200 justify-between gap-2"
                    style={selectedPillStyle}
                  >
                    <span className="leading-none">{objective}</span>
                    <button
                      type="button"
                      onClick={() => removeObjective(objective)}
                      className="flex-shrink-0 inline-flex items-center justify-center h-6 w-6 rounded-full text-base leading-none text-[#60A5FA] hover:text-[#185FA5] transition-colors"
                      aria-label={`Remove ${objective}`}
                    >
                      <span aria-hidden>×</span>
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </ChoiceGroup>

        {/* Project description (free text, required) — the objectives above are goal labels, not the project */}
        <div className="space-y-2" {...fieldAnchorProps('project_description')}>
          <div className="flex items-center gap-2">
            <Label htmlFor="project_description" className="font-medium" style={{ color: '#4B5563' }}>
              Describe the project <span style={{ color: '#4B5563' }}>*</span>
            </Label>
            <GlossaryHint term="Project" definition={glossaryFor('Project')?.definition} />
          </div>
          <p className="text-xs leading-relaxed" style={{ color: '#6B7280' }}>
            In your own words: what you will build or do, for whom, the scope, the rough budget split and when it starts and ends. The objectives you picked above are goal labels — they are not the project.
          </p>
          <Textarea
            id="project_description"
            name="project_description"
            placeholder="e.g. Build a production-ready AI compliance-monitoring module for our SaaS platform, covering rule ingestion, alerting and audit reports, for SMB customers in Singapore and Malaysia. 6-month build with two engineers; S$100k budget mostly on engineering salaries and cloud."
            rows={5}
            value={formData.project_description || ''}
            onChange={(e) => updateFormData({ project_description: e.target.value })}
            style={{ color: '#4B5563' }}
            className="bg-white/5 backdrop-blur-sm placeholder:text-gray-400 border border-white/20 focus:ring-2 focus:ring-white/40 focus:border-white/30 focus:bg-white/8 hover:bg-white/8 resize-none transition-all shadow-sm"
            minLength={PROJECT_DESCRIPTION_MIN_CHARS}
            required
            {...invalidInputProps('project_description', errors.project_description, 'project_description_hint')}
          />
          <p id="project_description_hint" className="text-xs leading-relaxed" style={{ color: '#6B7280' }}>
            At least {PROJECT_DESCRIPTION_MIN_CHARS} characters is preferred so OPTRA can assess the scope, timeline, budget and intended outcome clearly.
          </p>
          <FieldError name="project_description" message={errors.project_description} />
        </div>

        {/* Expected Outcomes */}
        <div className="space-y-2" {...fieldAnchorProps('expected_outcomes')}>
          <Label htmlFor="outcomes" className="font-medium" style={{ color: '#4B5563' }}>
            Expected Outcomes <span style={{ color: '#4B5563' }}>*</span>
          </Label>
          <Textarea
            id="outcomes"
            name="expected_outcomes"
            placeholder="Describe the expected outcomes and benefits of your project..."
            rows={4}
            value={formData.expected_outcomes || ''}
            onChange={(e) => updateFormData({ expected_outcomes: e.target.value })}
            style={{ color: '#4B5563' }}
            className="bg-white/5 backdrop-blur-sm placeholder:text-gray-400 border border-white/20 focus:ring-2 focus:ring-white/40 focus:border-white/30 focus:bg-white/8 hover:bg-white/8 resize-none transition-all shadow-sm"
            {...invalidInputProps('expected_outcomes', errors.expected_outcomes)}
          />
          <FieldError name="expected_outcomes" message={errors.expected_outcomes} />
        </div>

        {/* Co-funding: value is committed only after the user moves the slider or picks a preset */}
        <ChoiceGroup
          name="cofunding_capacity"
          role="group"
          legend="Co-funding Capacity"
          required
          hint="The share of project cost your company can pay itself. Drag the slider or pick a preset; nothing is selected until you do."
          error={errors.cofunding_capacity}
          className="space-y-4"
          optionsClassName="space-y-3"
        >
          <div className="flex items-center gap-2 -mt-2">
            <GlossaryHint term="Co-funding capacity" definition={glossaryFor('Co-funding capacity')?.definition} />
            <span className="text-xs" style={{ color: '#6B7280' }}>What counts as co-funding?</span>
          </div>
          <div className="px-4">
            <Slider
              value={[formData.cofunding_capacity ?? 0]}
              onValueChange={([value]) =>
                updateFormData({ cofunding_capacity: Math.round(value) })
              }
              max={100}
              step={1}
              className="w-full"
              fillDirection="ltr"
              fillColor="#93C5FD"
              trackColor="rgba(148, 163, 184, 0.28)"
              aria-label="Co-funding capacity, percentage of project cost"
              aria-valuetext={
                formData.cofunding_capacity == null
                  ? 'Not set'
                  : `${formData.cofunding_capacity} percent of project cost`
              }
              aria-describedby="cofunding_capacity-hint"
            />
            <div className="flex justify-between text-sm mt-2" style={{ color: '#4B5563' }}>
              <span aria-hidden>0%</span>
              <span className="font-medium" style={{ color: '#60A5FA' }} data-testid="cofunding-value" aria-live="polite">
                {formData.cofunding_capacity == null
                  ? 'Not set yet (required)'
                  : `${formData.cofunding_capacity}% of project cost`}
              </span>
              <span aria-hidden>100%</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 px-4" data-testid="cofunding-presets">
            <span className="text-xs" style={{ color: '#6B7280' }}>Quick pick:</span>
            {COFUNDING_QUICK_PICKS.map((pct) => {
              const active = formData.cofunding_capacity === pct;
              return (
                <button
                  key={pct}
                  type="button"
                  aria-pressed={active}
                  onClick={() => updateFormData({ cofunding_capacity: pct })}
                  className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                  style={active ? selectedPillStyle : pillStyle}
                >
                  {pct}%
                </button>
              );
            })}
          </div>
        </ChoiceGroup>

        {/* Vendor & procurement readiness */}
        <div className="space-y-6 mt-6">
          <div className="space-y-2">
            <p className="font-medium" style={{ color: '#4B5563' }}>
              Vendor & Procurement
            </p>
            <p className="text-sm" style={{ color: '#4B5563' }}>
              Vendor details help us assess procurement readiness and related-party risks.
            </p>
          </div>

          {/* Full-width primary vendor selection question */}
          <LabelledSelect
            name="vendor_selected"
            label="Have you selected a vendor for this project?"
            value={formData.vendor_selected ?? ''}
            onChange={(value) => updateFormData({ vendor_selected: value })}
            options={['Yes', 'No']}
            placeholder="Select an option"
            error={errors.vendor_selected}
          />

          {/* Follow-up block stacked below when vendor is selected */}
          {formData.vendor_selected === 'Yes' && (
            <div className="space-y-4">
              <LabelledSelect
                name="vendor_country"
                label="Where is the vendor based?"
                value={formData.vendor_country || ''}
                onChange={(value) => updateFormData({ vendor_country: value })}
                options={vendorCountryOptions}
                placeholder="Select vendor location"
                error={errors.vendor_country}
              />
              <LabelledSelect
                name="vendor_related_party"
                label="Is the vendor related to your company, directors, or shareholders?"
                value={formData.vendor_related_party || ''}
                onChange={(value) => updateFormData({ vendor_related_party: value })}
                options={['Yes', 'No', 'Not sure']}
                placeholder="Select an option"
                error={errors.vendor_related_party}
              />
            </div>
          )}

          <LabelledSelect
            name="quotation_count"
            label="Number of vendor quotations"
            value={formData.quotation_count || ''}
            onChange={(value) => updateFormData({ quotation_count: value })}
            options={quotationCountOptions}
            placeholder="How many quotations do you currently have?"
            error={errors.quotation_count}
          />
        </div>

        {/* Funding fit & priorities */}
        <div className="space-y-6 mt-8">
          <div>
            <p className="font-medium text-lg" style={{ color: '#4B5563' }}>
              Funding Fit & Priorities
            </p>
          </div>

          {/* All questions full-width, stacked */}
          <div className="space-y-4">
            <LabelledSelect
              name="can_fund_upfront"
              label="Can your company pay upfront for the project before reimbursement?"
              value={formData.can_fund_upfront || ''}
              onChange={(value) => updateFormData({ can_fund_upfront: value })}
              options={canFundUpfrontOptions}
              placeholder="Select an option"
              error={errors.can_fund_upfront}
            />
            <LabelledSelect
              name="application_priority"
              label="What matters most for this application?"
              value={formData.application_priority || ''}
              onChange={(value) => updateFormData({ application_priority: value })}
              options={applicationPriorityOptions}
              placeholder="Select your priority"
              error={errors.application_priority}
            />
            <LabelledSelect
              name="scope_flexibility"
              label="Would you be open to adjusting the project scope or structure to improve eligibility?"
              value={formData.scope_flexibility || ''}
              onChange={(value) => updateFormData({ scope_flexibility: value })}
              options={scopeFlexibilityOptions}
              placeholder="Select an option"
              error={errors.scope_flexibility}
            />
          </div>
        </div>

        {/* Market expansion (conditional use by backend; always optional here) */}
        <div className="space-y-6 mt-8">
          <div>
            <p className="font-medium text-lg" style={{ color: '#4B5563' }}>
              Overseas Expansion (optional)
            </p>
          </div>
          <p className="text-sm" style={{ color: '#4B5563' }}>
            If this project relates to overseas expansion, share a bit more. If not, you can leave these blank.
          </p>

          {/* All expansion questions stacked full-width */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="target_market" className="font-medium" style={{ color: '#4B5563' }}>
                Target overseas market
              </Label>
              <Input
                id="target_market"
                name="target_market"
                type="text"
                placeholder="e.g., Indonesia, Not applicable"
                value={formData.target_market || ''}
                onChange={(e) => updateFormData({ target_market: e.target.value })}
                style={INPUT_BOX_STYLE}
                className={INPUT_BOX_CLASS}
              />
            </div>

            <LabelledSelect
              name="existing_target_market_presence"
              label="Existing presence in target market"
              required={false}
              value={formData.existing_target_market_presence || ''}
              onChange={(value) => updateFormData({ existing_target_market_presence: value })}
              options={[
                'No presence',
                'Existing customers',
                'Distributor / partner',
                'Staff / office',
                'Other presence',
                'Not applicable',
              ]}
              placeholder="Select current presence"
            />

          </div>

            {/* Overseas expansion activities as checklist */}
            <ChoiceGroup
              name="expansion_activity_types"
              role="group"
              legend="Overseas expansion activities (optional)"
              hint="Select all activities that apply. Choose “Not applicable” if this is not an expansion project."
              className="space-y-2"
              optionsClassName="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1"
            >
                {[
                  "Market research",
                  "Overseas marketing",
                  "Trade fair / exhibition",
                  "Business matching / partner search",
                  "Overseas set-up / incorporation",
                  "Distributor / channel development",
                  "Other",
                  "Not applicable",
                ].map((activity) => {
                  const checked = formData.expansion_activity_types?.includes(activity);
                  const notApplicableSelected = formData.expansion_activity_types?.includes("Not applicable");
                  const faded = activity !== "Not applicable" && notApplicableSelected;
                  return (
                    <div
                      key={activity}
                      className="transition-opacity"
                      style={faded ? ELIGIBILITY_PEER_DIM_STYLE : undefined}
                    >
                      <ChecklistItem
                        id={`exp-${activity}`}
                        label={activity}
                        checked={checked}
                        onCheckedChange={(isChecked) => {
                          const current = formData.expansion_activity_types || [];
                          let next;
                          if (isChecked) {
                            if (activity === "Not applicable") {
                              next = ["Not applicable"];
                            } else {
                              next = current
                                .filter((a) => a !== "Not applicable")
                                .concat(activity);
                            }
                          } else {
                            next = current.filter((a) => a !== activity);
                          }
                          updateFormData({ expansion_activity_types: next });
                        }}
                        compact={true}
                      />
                    </div>
                  );
                })}
            </ChoiceGroup>
        </div>
        </div>
      </div>
    </div>
  );
}
